<template>
  <!-- Admin Utilities — no Figma frame; built in the Service & Repair card style.
       Items and flows follow the Android technician app (AdminUtilsFragment): Update Order (price / summary /
       installation date / address), Get Customer Phone Verification Code, Heat Load Calculator. APIs: api/v3/pro/utilities/*. -->
  <div class="w-full font-roboto">
    <div v-if="!isAllowed" class="auCard text-center py-[40px]">
      <p class="m-0 text-[16px] text-[#333333]">Admin Utilities is not available for your account.</p>
    </div>

    <div v-else class="grid grid-cols-1 laptop:grid-cols-2 gap-[20px] items-start">
      <!-- Update Order (Android UpdateOrderFragment) -->
      <section class="auCard laptop:row-span-2">
        <div class="auTitle">
          <span class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <span class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <h2>Update Order</h2>
        </div>
        <p class="auHint">To update an order, please enter the Order ID below</p>
        <label class="auLabel" for="auOrderId">Order ID</label>
        <input id="auOrderId" v-model.trim="orderId" type="text" inputmode="numeric" maxlength="12"
          placeholder="Enter ID" class="auInput" @input="orderId = orderId.replace(/\D/g, '')">

        <div class="grid grid-cols-1 tablet:grid-cols-2 gap-[12px] mt-[20px]">
          <button v-for="action in orderActions" :key="action.key" type="button" class="auTile" @click="runOrderAction(action.key)">
            <span class="auTileIcon"><img :src="action.icon" alt=""></span>
            <span class="text-left">
              <span class="block text-[15px] font-medium text-[#333333]">{{ action.label }}</span>
              <span class="block text-[12px] text-[#666666] mt-[2px]">{{ action.hint }}</span>
            </span>
          </button>
        </div>
      </section>

      <!-- Get Customer Phone Verification Code (Android GetCustomerPhoneVerificationCodeFragment) -->
      <section class="auCard">
        <div class="auTitle">
          <span class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <span class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <h2>Customer Phone Verification Code</h2>
        </div>
        <p class="auHint">Please enter consumer's phone number to obtain the verification code</p>
        <label class="auLabel" for="auPhone">Phone Number</label>
        <div class="flex flex-col tablet:flex-row gap-[10px]">
          <input id="auPhone" v-model="phone" v-maska="'(###) ###-####'" type="tel" placeholder="Enter Number"
            class="auInput tablet:flex-1" @keyup.enter="getVerificationCode">
          <button type="button" class="fxBtnPrimary" :disabled="busy" @click="getVerificationCode">Get Code</button>
        </div>
        <div v-if="verificationCode" class="mt-[15px] rounded-[10px] border-1 border-[#C3DAFB] bg-[#EAF2FE] px-[16px] py-[12px] flex items-center justify-between gap-[10px]">
          <div>
            <span class="block text-[13px] text-[#2A5BA8]">Verification Code</span>
            <span class="block text-[24px] font-semibold tracking-[4px] text-[#333333]">{{ verificationCode }}</span>
          </div>
          <button type="button" class="fxBtnSecondary !px-[15px]" @click="copyCode">Copy</button>
        </div>
      </section>

      <!-- Heat Load Calculator (Android opens the page in the browser) -->
      <section class="auCard">
        <div class="auTitle">
          <span class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
          <span class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
          <h2>Heat Load Calculator</h2>
        </div>
        <div class="flex flex-col tablet:flex-row tablet:items-center gap-[15px]">
          <span class="auTileIcon"><img :src="icons.heatLoad" alt=""></span>
          <p class="m-0 flex-1 text-[14px] text-[#666666]">Estimate the heating and cooling load of a home before designing a system.</p>
          <a :href="heatLoadUrl" target="_blank" rel="noopener noreferrer" class="fxBtnPrimary no-underline">Open Calculator</a>
        </div>
      </section>
    </div>

    <!-- one modal, content by type -->
    <div ref="auModal" class="modal fade fxModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable mobile:mx-[16px] tablet:mx-auto"
        :class="modal.type === 'frame' ? 'max-w-full tablet:!max-w-[90vw] largeDesktop:!max-w-[1200px]' : 'max-w-full tablet:!max-w-[560px]'">
        <div class="modal-content">
          <div class="modal-body !p-[15px] font-roboto">
            <FxModalHeader :title="modal.title" :subtitle="modal.orderId ? `Order ID: ${modal.orderId}` : ''" :dismiss="false" @close="closeModal" />

            <!-- Edit Summary (server summary page) -->
            <iframe v-if="modal.type === 'frame' && modal.url" :src="modal.url" :title="modal.title"
              class="w-full h-[75vh] border-0 rounded-[8px]" />

            <!-- Update Order Price (Android UpdateOrderPriceFragment) -->
            <template v-else-if="modal.type === 'price'">
              <div class="grid grid-cols-1 tablet:grid-cols-2 gap-[12px]">
                <div>
                  <span class="auLabel">Order Price</span>
                  <p class="auValue">{{ money(price.total) }}</p>
                </div>
                <div>
                  <span class="auLabel">Payment Type</span>
                  <p class="auValue">{{ currentPaymentTypeText }}</p>
                </div>
              </div>
              <label class="auLabel mt-[15px]" for="auNewPrice">New Price ($) <span class="text-[#EE5851]">*</span></label>
              <input id="auNewPrice" v-model="form.newPrice" type="text" inputmode="decimal" placeholder="Price" class="auInput"
                @input="form.newPrice = form.newPrice.replace(/[^\d.]/g, '')">
              <p class="m-0 mt-[4px] text-[12px] text-[#666666]">(This price is exclusive of taxes)</p>
              <label class="auLabel mt-[15px]" for="auPaymentType">New Payment Type <span class="text-[#EE5851]">*</span></label>
              <FxSelect v-model="form.paymentType" :options="price.payment_types || []" label-key="value" value-key="key"
                placeholder="Select payment type" inline />
              <div class="flex justify-center gap-[10px] mt-[20px]">
                <button type="button" class="fxDeclineCancel" @click="closeModal">Cancel</button>
                <button type="button" class="fxBtnPrimary" :disabled="busy" @click="submitPrice">Submit</button>
              </div>
            </template>

            <!-- Change Order Installation Date (Android ChangeOrderInstallationDateFragment) -->
            <template v-else-if="modal.type === 'dates'">
              <div class="grid grid-cols-1 tablet:grid-cols-2 gap-[12px]">
                <div v-for="field in dateFields" :key="field.key">
                  <label class="auLabel" :for="'au-' + field.key">{{ field.label }}</label>
                  <input :id="'au-' + field.key" v-model="form[field.key]" :type="field.withTime ? 'datetime-local' : 'date'"
                    :step="field.withTime ? 900 : null" :min="field.withTime ? minDateTime : minDate" class="auInput">
                </div>
              </div>
              <p class="m-0 mt-[8px] text-[12px] text-[#666666]">Inspection and installation times are between 7:00 AM and 9:00 PM.</p>
              <div class="flex justify-center gap-[10px] mt-[20px]">
                <button type="button" class="fxDeclineCancel" @click="closeModal">Cancel</button>
                <button type="button" class="fxBtnPrimary" :disabled="busy" @click="submitDates">Update</button>
              </div>
            </template>

            <!-- Update Order Address (Android UpdateOrderAddressFragment) -->
            <template v-else-if="modal.type === 'address'">
              <label class="auLabel">Street Address <span class="text-[#EE5851]">*</span></label>
              <GoogleAutoComplete v-model="form.address" placeholder="Enter Address"
                :input-class="'w-full h-[38px] px-[12px] rounded-[8px] text-[14px] text-[#333333] outline-none'"
                @place-selected="handlePlace" />
              <div class="grid grid-cols-1 tablet:grid-cols-3 gap-[12px] mt-[12px]">
                <div>
                  <label class="auLabel" for="auCity">City <span class="text-[#EE5851]">*</span></label>
                  <input id="auCity" v-model="form.city" type="text" placeholder="Enter City" class="auInput">
                </div>
                <div>
                  <label class="auLabel" for="auState">State <span class="text-[#EE5851]">*</span></label>
                  <input id="auState" v-model="form.state" type="text" placeholder="Enter State" class="auInput">
                </div>
                <div>
                  <label class="auLabel" for="auZip">Zip Code <span class="text-[#EE5851]">*</span></label>
                  <input id="auZip" v-model="form.postal_code" type="text" placeholder="Enter Zip Code" class="auInput">
                </div>
              </div>
              <div class="flex justify-center gap-[10px] mt-[20px]">
                <button type="button" class="fxDeclineCancel" @click="closeModal">Cancel</button>
                <button type="button" class="fxBtnPrimary" :disabled="busy" @click="submitAddress">Update</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { mapGetters } from "vuex";
import { useToast } from "vue-toastification";
import { vMaska } from "maska/vue";
import FxModalHeader from "../../components/nest_components/FxModalHeader.vue";
import GoogleAutoComplete from "../../components/partialComponent/GoogleAutoComplete.vue";
import FxSelect from "../../components/partialComponent/FxSelect.vue";
import { adminUtilitiesApi, HEAT_LOAD_CALCULATOR_URL } from "../../services/adminUtilitiesApi";
import { menuHasRoute } from "../../helpers/techMenu";
import priceIcon from "../../assets/images/admin-utilities/price.png";
import summaryIcon from "../../assets/images/admin-utilities/edit-summary.png";
import calendarIcon from "../../assets/images/admin-utilities/calendar-black.png";
import mapIcon from "../../assets/images/admin-utilities/map.png";
import heatLoadIcon from "../../assets/images/admin-utilities/heat-load.png";

const toast = useToast();

const pad = (n) => String(n).padStart(2, "0");
// unix seconds → value for <input type="datetime-local"> / <input type="date"> (local time)
const toInputValue = (seconds, withTime) => {
  const n = Number(seconds);
  if (!n) return "";
  const d = new Date(n * 1000);
  const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${day}T${pad(d.getHours())}:${pad(d.getMinutes())}` : day;
};
// input value → Date (local); a date-only value keeps the current time of day, like the Android date picker
const fromInputValue = (value, withTime) => {
  if (!value) return null;
  if (withTime) return new Date(value);
  const [y, m, d] = value.split("-").map(Number);
  const now = new Date();
  return new Date(y, m - 1, d, now.getHours(), now.getMinutes());
};

export default {
  name: "AdminUtilities",
  components: { FxModalHeader, GoogleAutoComplete, FxSelect },
  directives: { maska: vMaska },
  data() {
    return {
      orderId: "",
      phone: "",
      verificationCode: "",
      busy: false,
      heatLoadUrl: HEAT_LOAD_CALCULATOR_URL,
      icons: { heatLoad: heatLoadIcon },
      orderActions: [
        { key: "price", label: "Update Price", hint: "New price and payment type", icon: priceIcon },
        { key: "summary", label: "Edit Summary", hint: "Open the system summary", icon: summaryIcon },
        { key: "dates", label: "Change Installation Date", hint: "Inspection, installation and invoice dates", icon: calendarIcon },
        { key: "address", label: "Update Address", hint: "Service address of the order", icon: mapIcon },
      ],
      dateFields: [
        { key: "inspection_datetime", label: "Inspection Date", withTime: true },
        { key: "installation_datetime", label: "Installation Date", withTime: true },
        { key: "invoice_datetime", label: "Invoice Date", withTime: false },
        { key: "invoice_paid_datetime", label: "Invoice Paid Date", withTime: false },
      ],
      modal: { type: "", title: "", url: "", orderId: "" },
      form: {},
      price: {},
      dates: {},
      address: {},
    };
  },
  computed: {
    ...mapGetters(["sideMenuData"]),
    // the side menu is role-filtered on the server (G-25); a direct URL follows it once the menu has loaded
    isAllowed() {
      return !Array.isArray(this.sideMenuData) || menuHasRoute(this.sideMenuData, "/admin-utilities");
    },
    currentPaymentTypeText() {
      const p = this.price;
      const match = (p.payment_types || []).find(t => (t.key || "").toLowerCase() === (p.preferred_payment_type || "").toLowerCase());
      return match?.value || p.preferred_payment_type_text || (p.preferred_payment_type || "-").toUpperCase();
    },
    minDate() {
      return toInputValue(Date.now() / 1000, false);
    },
    minDateTime() {
      return `${this.minDate}T00:00`;
    },
  },
  beforeUnmount() {
    this.bsModal?.dispose();
  },
  methods: {
    money(value) {
      const n = Number(value) || 0;
      return `$ ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    errorOf(res, fallback) {
      return (typeof res.body === "string" && res.body) || res.message || fallback;
    },
    showModal() {
      this.$nextTick(() => {
        this.bsModal = this.bsModal || new Modal(this.$refs.auModal);
        this.bsModal.show();
      });
    },
    closeModal() {
      this.bsModal?.hide();
      this.modal = { type: "", title: "", url: "", orderId: "" };
    },
    openModal(type, title, orderId, url = "") {
      this.modal = { type, title, url, orderId: String(orderId || "") };
      this.showModal();
    },

    // Android UpdateOrderFragment: every action needs the Order ID first
    runOrderAction(key) {
      if (!this.orderId) {
        toast.error("Order ID must be entered to perform this operation");
        return;
      }
      if (key === "price") return this.openPrice();
      if (key === "summary") return this.openSummary();
      if (key === "dates") return this.openDates();
      if (key === "address") return this.openAddress();
    },

    async openPrice() {
      const res = await adminUtilitiesApi.orderPrice(this.orderId);
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Unable to get the order price."));
      this.setPrice(res.body);
      this.openModal("price", "Update Order Price", res.body.id);
    },
    setPrice(body) {
      this.price = body || {};
      const preferred = (this.price.preferred_payment_type || "").toLowerCase();
      const match = (this.price.payment_types || []).find(t => (t.key || "").toLowerCase() === preferred);
      this.form = {
        newPrice: (Number(this.price.total) || 0).toFixed(2),
        paymentType: match ? match.key : "",
      };
    },
    async submitPrice() {
      if (!this.form.newPrice) return toast.error("Enter New Price");
      if (!this.form.paymentType) return toast.error("Enter Preferred Payment Type");
      this.busy = true;
      const res = await adminUtilitiesApi.updateOrderPrice(this.price.id, this.form.newPrice.replace(/[$,]/g, ""), this.form.paymentType);
      this.busy = false;
      if (!res.ok) return toast.error(this.errorOf(res, "Unable to update the price."));
      this.setPrice(res.body);
      toast.success(res.message || "Price has been updated successfully.");
    },

    async openSummary() {
      const res = await adminUtilitiesApi.orderSummaryEdit(this.orderId);
      if (!res.ok || !res.body?.url) return toast.error(this.errorOf(res, "Oops! Something Went Wrong"));
      this.openModal("frame", "System Summary", res.body.id, res.body.url);
    },

    async openDates() {
      const res = await adminUtilitiesApi.orderDates(this.orderId);
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Unable to get the order dates."));
      this.dates = res.body;
      this.form = {};
      this.dateFields.forEach(f => { this.form[f.key] = toInputValue(res.body[f.key], f.withTime); });
      this.openModal("dates", "Change Order Installation Date", res.body.id);
    },
    async submitDates() {
      const params = {};
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      for (const f of this.dateFields) {
        const value = this.form[f.key];
        const original = this.dates[f.key];
        // unchanged → send what the server gave (Android sends the stored value back)
        if (value === toInputValue(original, f.withTime)) {
          if (Number(original)) params[f.key] = String(original);
          continue;
        }
        const date = fromInputValue(value, f.withTime);
        if (!date) continue;
        if (date < todayStart) return toast.error(`${f.label} cannot be in the past.`);
        if (f.withTime && (date.getHours() < 7 || date.getHours() > 21 || (date.getHours() === 21 && date.getMinutes() > 0))) {
          return toast.error(`${f.label} time must be between 7:00 AM and 9:00 PM.`);
        }
        params[f.key] = String(Math.floor(date.getTime() / 1000));
      }
      this.busy = true;
      const res = await adminUtilitiesApi.updateOrderDates(this.dates.id, params);
      this.busy = false;
      if (!res.ok) return toast.error(this.errorOf(res, "Unable to update the dates."));
      Object.assign(this.dates, params);
      toast.success(res.body?.msg || res.message || "Dates have been updated successfully.");
    },

    async openAddress() {
      const res = await adminUtilitiesApi.orderAddress(this.orderId);
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Unable to get the order address."));
      this.setAddress(res.body);
      this.openModal("address", "Update Order Address", res.body.order_id);
    },
    setAddress(body) {
      this.address = body || {};
      this.form = {
        address: this.address.address || "",
        city: this.address.city || "",
        state: this.address.state || "",
        postal_code: this.address.postal_code || "",
        lat: null,
        long: null,
      };
    },
    // Google place → address parts (Android getAddress → formatted/address/get)
    async handlePlace(place) {
      const lat = place?.geometry?.location?.lat?.();
      const lng = place?.geometry?.location?.lng?.();
      const res = await adminUtilitiesApi.formattedAddress({ address: place?.formatted_address, lat, lng });
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "Some thing went wrong"));
      this.form = {
        ...this.form,
        address: res.body.address_1 || place?.formatted_address || this.form.address,
        city: res.body.city_id || "",
        state: res.body.state_id || "",
        postal_code: res.body.postal_code || "",
        lat: lat ?? null,
        long: lng ?? null,
      };
    },
    async submitAddress() {
      const f = this.form;
      if (!f.address?.trim()) return toast.error("Please enter street address to proceed further");
      if (!f.city?.trim()) return toast.error("Please enter city name to proceed further");
      if (!f.state?.trim()) return toast.error("Please enter the state to proceed further");
      if (!f.postal_code?.trim()) return toast.error("Please enter the zip code to proceed further");
      this.busy = true;
      const res = await adminUtilitiesApi.updateOrderAddress(this.address.order_id, {
        id: this.address.id,
        order_id: this.address.order_id,
        address: f.address.trim(),
        lat: f.lat ?? this.address.lat,
        long: f.long ?? this.address.long,
        city: f.city.trim(),
        state: f.state.trim(),
        postal_code: f.postal_code.trim(),
      });
      this.busy = false;
      if (!res.ok) return toast.error(this.errorOf(res, "Unable to update the address."));
      this.setAddress(res.body);
      toast.success(res.message || "Address has been updated successfully.");
    },

    async getVerificationCode() {
      this.verificationCode = "";
      if (!this.phone.trim()) return toast.error("Phone Number must be entered to perform this operation");
      this.busy = true;
      const res = await adminUtilitiesApi.verificationCode(this.phone);
      this.busy = false;
      if (!res.ok || !res.body) return toast.error(this.errorOf(res, "No verification code found against this number"));
      this.verificationCode = String(res.body.code ?? "");
    },
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.verificationCode);
        toast.success("Verification code copied.");
      } catch (e) {
        toast.error("Unable to copy the code.");
      }
    },
  },
};
</script>

<style scoped>
.auCard {
  box-shadow: 0px 0px 10px 0px #0000001A;
  border-radius: 20px;
  padding: 20px 15px;
  background: #fff;
}

.auTitle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 15px;
}

.auTitle h2 {
  margin: 0;
  padding-left: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
}

.auHint {
  margin: 0 0 15px;
  font-size: 14px;
  color: #666666;
}

.auLabel {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: #EA8A8A;
}

.auValue {
  margin: 0;
  font-size: 16px;
  color: #333333;
}

.auInput {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #E2E5EC;
  border-radius: 8px;
  font-size: 14px;
  color: #333333;
  outline: none;
  background: #fff;
}

.auInput:focus {
  border-color: #7695FF;
}

.auTile {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid #EFEFEF;
  border-radius: 12px;
  background: #fff;
  transition: border-color .2s, box-shadow .2s;
}

.auTile:hover {
  border-color: #7695FF;
  box-shadow: 0px 0px 10px 0px #0000001A;
}

.auTileIcon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F4F5FB;
}

.auTileIcon img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

@media (min-width: 1024px) {
  .auCard {
    padding: 20px;
  }

  .auTitle h2 {
    font-size: 20px;
  }

  .auLabel {
    font-size: 13px;
  }
}

@media (min-width: 1536px) {
  .auLabel {
    font-size: 14px;
  }
}
</style>
